import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  FileText, Upload, Receipt, FileCheck, Trash2, ExternalLink, Plus, Minus,
} from 'lucide-react';
import {
  useCollaborations, STATUS_CONFIG, STATUS_ORDER, MESES, MESES_ORDEN,
} from '../context/CollaborationsContext';
import { orderUrl } from '../config';
import { apiFetch } from '../api';
import { calculateROAS, formatEuro, parseCurrency } from '../utils/helpers';
import { useAttachment } from '../hooks/useAttachment';
import AttachmentRow from './AttachmentRow';
import ArchivosColaboracion from './ArchivosColaboracion';
import CollabDetailModal from './CollabDetailModal';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';

function StatusBadge({ status, onChangeStatus }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['nueva'];
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, right: 0 });
  const badgeRef = React.useRef();

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        badgeRef.current &&
        !badgeRef.current.contains(e.target) &&
        !document.getElementById('status-dropdown')?.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleOpen(e) {
    e.stopPropagation();
    const rect = badgeRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setOpen((o) => !o);
  }

  return (
    <div ref={badgeRef} className="shrink-0">
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
        style={{ background: cfg.bg }}
      >
        {cfg.label}
      </button>
      {open &&
        createPortal(
          <div
            id="status-dropdown"
            className="fixed z-[9999] min-w-[10rem] rounded-md border bg-background p-1 shadow-md flex flex-col gap-0.5"
            style={{ top: pos.top, right: pos.right }}
          >
            {STATUS_ORDER.map((s) => {
              const c = STATUS_CONFIG[s];
              const isActive = s === status;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangeStatus(s);
                    setOpen(false);
                  }}
                  className={cn(
                    'text-left rounded-sm px-2.5 py-1.5 text-xs font-semibold transition-colors',
                    isActive ? 'text-white' : 'text-foreground hover:bg-muted',
                  )}
                  style={isActive ? { background: c.bg } : undefined}
                  disabled={isActive}
                >
                  {c.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

// Hermano de StatusBadge: mismo patrón (píldora + desplegable en portal) pero
// con un solo color, doce opciones y la de "sin mes". Se deja aparte en vez de
// generalizar StatusBadge porque las diferencias son más que lo compartido; si
// algún día hay un tercer tag, ese será el momento de extraer la base común.
function MonthBadge({ mes, onChangeMes }) {
  const etiqueta = MESES[mes];
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, right: 0 });
  const badgeRef = React.useRef();

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        badgeRef.current &&
        !badgeRef.current.contains(e.target) &&
        !document.getElementById('month-dropdown')?.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleOpen(e) {
    // Sin esto, el click llegaría a la cabecera de la tarjeta y abriría la ficha.
    e.stopPropagation();
    const rect = badgeRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setOpen((o) => !o);
  }

  function elegir(e, valor) {
    e.stopPropagation();
    onChangeMes(valor);
    setOpen(false);
  }

  return (
    <div ref={badgeRef} className="shrink-0">
      <button
        type="button"
        onClick={handleOpen}
        title="Mes de publicación"
        className={cn(
          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
          etiqueta ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground',
        )}
      >
        {etiqueta || 'Sin mes'}
      </button>
      {open &&
        createPortal(
          <div
            id="month-dropdown"
            className="fixed z-[9999] min-w-[9rem] rounded-md border bg-background p-1 shadow-md flex flex-col gap-0.5"
            style={{ top: pos.top, right: pos.right }}
          >
            {MESES_ORDEN.map((clave) => {
              const isActive = clave === mes;
              return (
                <button
                  key={clave}
                  type="button"
                  onClick={(e) => elegir(e, clave)}
                  className={cn(
                    'text-left rounded-sm px-2.5 py-1.5 text-xs font-semibold transition-colors',
                    isActive ? 'bg-blue-100 text-blue-700' : 'text-foreground hover:bg-muted',
                  )}
                  disabled={isActive}
                >
                  {MESES[clave]}
                </button>
              );
            })}
            {/* Quitar el mes: mismo sitio donde se puso, sin abrir la ficha. */}
            <button
              type="button"
              onClick={(e) => elegir(e, '')}
              className={cn(
                'text-left rounded-sm px-2.5 py-1.5 text-xs font-semibold transition-colors border-t mt-0.5 pt-1.5',
                !etiqueta ? 'bg-muted text-muted-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
              disabled={!etiqueta}
            >
              Sin mes
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}

function CurrencyField({ label, value, onSave, soloLectura, titulo }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');

  // El coste sale de los productos cuando los hay: dejar editarlo aquí solo
  // serviría para que el siguiente cambio de productos lo pisara.
  if (soloLectura) {
    return (
      <div className="rounded-md bg-muted/40 p-3" title={titulo}>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          {label}
        </div>
        <div className="text-sm font-semibold mt-1">{formatEuro(value)}</div>
      </div>
    );
  }

  function startEdit() {
    setRaw(value === 0 ? '' : String(value).replace('.', ','));
    setEditing(true);
  }

  function finishEdit() {
    const num = parseCurrency(raw);
    onSave(num);
    setEditing(false);
  }

  return (
    <div
      className="rounded-md bg-muted/40 p-3 cursor-pointer hover:bg-muted transition-colors"
      onClick={!editing ? startEdit : undefined}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </div>
      {editing ? (
        <Input
          autoFocus
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={finishEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') finishEdit();
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="0"
          className="h-7 mt-1 text-sm"
        />
      ) : (
        <div className="text-sm font-semibold mt-1">{formatEuro(value)}</div>
      )}
    </div>
  );
}

// Textos de cada adjunto. El correo que dispara cada uno es distinto, así que
// también lo es lo que hay que contarle al usuario cuando falla.
const CONTRACT_MESSAGES = {
  uploaded: (filename) => `Contrato "${filename}" enviado a legal`,
  uploadedButFailed: 'Contrato guardado, pero no se pudo avisar a legal',
  resent: 'Aviso enviado a legal',
  failedShort: 'No se avisó a legal',
};

const DRAFT_MESSAGES = {
  uploaded: () => 'Contrato generado y enviado al influencer',
  uploadedButFailed: 'Contrato generado, pero no se pudo enviar al influencer',
  resent: 'Contrato reenviado al influencer',
  failedShort: 'No se envió al influencer',
};

const INVOICE_MESSAGES = {
  uploaded: (filename) => `Factura "${filename}" enviada a administración`,
  uploadedButFailed: 'Factura guardada, pero no se pudo avisar a administración',
  resent: 'Factura reenviada a administración',
  failedShort: 'No se avisó a administración',
};

// Los cuatro botones de acción viven en una cuadrícula de dos columnas dentro
// de la tarjeta, que puede quedarse muy estrecha (a 4 columnas son ~94 px). Con
// el icono ya sin encogerse, se deja que la etiqueta pase a dos líneas y el
// botón crezca, en vez de que el texto se salga.
// Los botones de acciones van siempre en una línea. La etiqueta más larga
// ("Cambiar contrato") no cabe a dos columnas en una tarjeta estrecha, así que
// las columnas dependen del ancho de la propia tarjeta, no del de la ventana:
// apiladas hasta 19rem y a dos columnas a partir de ahí.
const CLASE_ACCION = 'h-8 gap-1.5 px-2.5';
const CLASE_ICONO = 'h-4 w-4';
const CLASE_ICONO_ACCION = 'h-3.5 w-3.5';

const SIGNED_MESSAGES = {
  uploaded: (filename) => `Firmado "${filename}" subido y pedido solicitado al equipo`,
  uploadedButFailed: 'Firmado guardado, pero no se pudo pedir el pedido',
  resent: 'Petición de pedido enviada',
  failedShort: 'No se pidió el pedido',
};

/**
 * Antes de generar el contrato se pregunta por la subida o el montaje, que no
 * vienen en el feed de productos. El importe se guarda aparte y se suma al
 * coste, así que sale igual en la tarjeta, en el contrato y en los correos.
 */
function DialogoServicios({ inicial, generando, onCerrar, onConfirmar }) {
  const [importe, setImporte] = useState(inicial ? String(inicial).replace('.', ',') : '');

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>¿Los productos tienen coste de subida o montaje?</DialogTitle>
          <DialogDescription>
            Si es así, introduce el importe total de los servicios.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirmar(parseCurrency(importe));
          }}
        >
          <div className="space-y-3">
            <Label htmlFor="coste-servicios">Importe de los servicios</Label>
            <Input
              id="coste-servicios"
              autoFocus
              inputMode="decimal"
              value={importe}
              onChange={(e) => setImporte(e.target.value)}
              placeholder="0,00 €"
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={generando} className="w-full">
              <FileText className={CLASE_ICONO} />
              {generando ? 'Creando contrato…' : 'Crear contrato'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CollaborationCard({ collab }) {
  const { updateCollaboration, applyCollaboration, deleteCollaboration } = useCollaborations();
  const [expanded, setExpanded] = useState(false);
  const [localNotes, setLocalNotes] = useState(collab.notes || '');
  const [showDetail, setShowDetail] = useState(false);
  const [editandoNotas, setEditandoNotas] = useState(false);
  const notesTimer = useRef(null);
  const notasPendientes = useRef(null);
  const notesRef = useRef(null);

  // La caja de notas crece con lo que se escribe. Hay que devolverla a 'auto'
  // antes de medir: si no, scrollHeight nunca baja del alto ya fijado y la caja
  // no encogería al borrar texto. En layout effect —y no en el onChange— para
  // que el alto inicial ya sea el bueno al pintar la tarjeta, sin salto. Se
  // suma el borde: con box-sizing border-box, scrollHeight no lo incluye y la
  // caja se quedaría 2 px corta, con la última línea recortada.
  useLayoutEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [localNotes]);

  const contract = useAttachment(collab, 'contract', CONTRACT_MESSAGES);
  const signed = useAttachment(collab, 'signed', SIGNED_MESSAGES);
  const invoice = useAttachment(collab, 'invoice', INVOICE_MESSAGES);
  // El contrato generado no se sube: solo se descarga y se reenvía.
  const draft = useAttachment(collab, 'draft', DRAFT_MESSAGES);
  const [generating, setGenerating] = useState(false);
  const [pidiendoServicios, setPidiendoServicios] = useState(false);

  const tieneProductos = Array.isArray(collab.productos) && collab.productos.length > 0;
  const roas = calculateROAS(collab.income, collab.cost);

  function handleStatusChange(newStatus) {
    updateCollaboration(collab.id, { status: newStatus });
    toast.success(`Estado actualizado a "${STATUS_CONFIG[newStatus]?.label}"`);
  }

  function handleMesChange(nuevoMes) {
    updateCollaboration(collab.id, { mesPublicacion: nuevoMes });
    toast.success(nuevoMes ? `Mes de publicación: ${MESES[nuevoMes]}` : 'Mes de publicación sin asignar');
  }

  // Las notas se guardan solas: 800 ms después de la última tecla o al salir
  // del campo, lo que llegue antes. `notasPendientes` es lo escrito que aún no
  // se ha mandado, para no guardar dos veces lo mismo.
  const guardarNotas = useCallback(() => {
    clearTimeout(notesTimer.current);
    if (notasPendientes.current === null) return;
    const val = notasPendientes.current;
    notasPendientes.current = null;
    updateCollaboration(collab.id, { notes: val });
  }, [collab.id, updateCollaboration]);

  function handleNotesChange(e) {
    const val = e.target.value;
    setLocalNotes(val);
    notasPendientes.current = val;
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(guardarNotas, 800);
  }

  const notasApagadas = !localNotes && !editandoNotas;

  function handleNotesBlur() {
    setEditandoNotas(false);
    guardarNotas();
  }

  // Si la tarjeta desaparece (cambio de sección, filtro) con algo sin guardar,
  // se guarda en ese momento.
  const guardarNotasAlSalir = useRef(guardarNotas);
  guardarNotasAlSalir.current = guardarNotas;
  useEffect(() => () => guardarNotasAlSalir.current(), []);

  function handleSaveIncome(num) {
    updateCollaboration(collab.id, { income: num });
    toast.success('Ingresos actualizados');
  }
  function handleSaveCost(num) {
    updateCollaboration(collab.id, { cost: num });
    toast.success('Coste actualizado');
  }

  async function handleGenerateContract(costeServicios) {
    setGenerating(true);
    try {
      const { collaboration, notification, productoError } = await apiFetch(
        `/api/collaborations/${collab.id}/actions/crear-contrato`,
        { method: 'POST', body: JSON.stringify({ costeServicios }) },
      );
      applyCollaboration(collaboration);
      setPidiendoServicios(false);
      if (notification.status === 'sent') {
        toast.success(DRAFT_MESSAGES.uploaded());
      } else {
        toast.warning(DRAFT_MESSAGES.uploadedButFailed, { description: notification.error });
      }
      // El contrato sale igualmente, pero sin los datos del producto.
      if (productoError) {
        toast.warning('El contrato va sin datos del producto', { description: productoError });
      }
    } catch (err) {
      toast.error(`No se pudo crear el contrato: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  }

  function handleDelete() {
    if (window.confirm(`¿Eliminar la colaboración con ${collab.name}?`)) {
      deleteCollaboration(collab.id);
      toast.error(`Colaboración con ${collab.name} eliminada`);
    }
  }

  return (
    <>
      <Card className="overflow-hidden flex flex-col">
        <div
          className="p-4 cursor-pointer"
          onClick={() => setShowDetail(true)}
        >
          <div className="min-w-0">
            <div className="font-medium truncate" title={collab.name}>
              {collab.name}
            </div>
            {collab.usuario && (
              <div className="text-xs text-muted-foreground truncate">
                {collab.usuario}
              </div>
            )}
            {/* Los tags van debajo del nombre, a la izquierda y siempre en una
                sola línea: en la tarjeta más estrecha se desplazan en
                horizontal en vez de partirse en dos filas. */}
            <div className="mt-2 flex items-center gap-1.5 overflow-x-auto">
              <StatusBadge status={collab.status} onChangeStatus={handleStatusChange} />
              <MonthBadge mes={collab.mesPublicacion} onChangeMes={handleMesChange} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 px-4 pb-4">
          <CurrencyField label="Ingresos" value={collab.income} onSave={handleSaveIncome} />
          <CurrencyField
            label="Coste"
            value={collab.cost}
            onSave={handleSaveCost}
            soloLectura={tieneProductos}
            titulo={tieneProductos ? 'Se calcula con los productos de la colaboración' : undefined}
          />
          {roas !== null && (
            <>
              <div className="rounded-md bg-muted/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  ROAS
                </div>
                <div
                  className="text-sm font-semibold mt-1"
                  style={{ color: Number(roas) >= 1 ? 'var(--success)' : 'var(--destructive)' }}
                >
                  {roas}
                </div>
              </div>
              <div className="rounded-md bg-muted/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Beneficio
                </div>
                <div className="text-sm font-semibold mt-1">
                  {formatEuro(collab.income - collab.cost)}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Notas, siempre a la vista y sin botón de guardar. Vacías y sin foco
            se ven apagadas, en una línea. Fuera de la cabecera, así que pulsar
            aquí no abre el detalle. */}
        <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
          <Textarea
            ref={notesRef}
            rows={1}
            aria-label="Notas"
            placeholder="Añadir nota..."
            value={localNotes}
            onChange={handleNotesChange}
            onFocus={() => setEditandoNotas(true)}
            onBlur={handleNotesBlur}
            className={cn(
              'min-h-0 text-sm resize-none overflow-hidden',
              notasApagadas && 'bg-muted/40 shadow-none text-muted-foreground cursor-text',
            )}
            // El color del borde va en línea: la regla global `* { border-color }`
            // de tailwind.input.css no tiene capa y gana a `border-transparent`.
            style={notasApagadas ? { borderColor: 'transparent' } : undefined}
          />
        </div>

        <div className="flex border-t mt-auto">
          {collab.pedido && (
            <a
              href={orderUrl(collab.pedido)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-primary border-r transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Ver pedido
            </a>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((p) => !p);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
          >
            {expanded ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
            {expanded ? 'Cerrar' : 'Gestionar'}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden border-t"
            >
              <div className="@container p-4 space-y-4">
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Acciones
                  </div>
                  <div className="grid grid-cols-1 gap-2 @[19rem]:grid-cols-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className={CLASE_ACCION}
                      disabled={generating}
                      onClick={() => setPidiendoServicios(true)}
                    >
                      <FileText className={CLASE_ICONO_ACCION} />
                      {generating ? 'Generando…' : draft.file ? 'Rehacer contrato' : 'Crear contrato'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={CLASE_ACCION}
                      disabled={contract.busy}
                      onClick={contract.pick}
                    >
                      <Upload className={CLASE_ICONO_ACCION} />
                      {contract.busy
                        ? 'Enviando…'
                        : contract.file
                        ? 'Cambiar contrato'
                        : 'Subir contrato'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={CLASE_ACCION}
                      disabled={invoice.busy}
                      onClick={invoice.pick}
                    >
                      <Receipt className={CLASE_ICONO_ACCION} />
                      {invoice.busy
                        ? 'Enviando…'
                        : invoice.file
                        ? 'Cambiar factura'
                        : 'Subir factura'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={CLASE_ACCION}
                      disabled={signed.busy}
                      onClick={signed.pick}
                    >
                      <FileCheck className={CLASE_ICONO_ACCION} />
                      {signed.busy
                        ? 'Enviando…'
                        : signed.file
                        ? 'Cambiar firmado'
                        : 'Subir firmado'}
                    </Button>
                  </div>

                  <div className="space-y-2 mt-2">
                    {/* El contrato para firmar desaparece en cuanto llega el
                        firmado: es el mismo documento, ya devuelto. */}
                    <AttachmentRow
                      attachment={signed.file ? null : draft.file}
                      busy={draft.busy}
                      iconClass="text-primary"
                      failedLabel={DRAFT_MESSAGES.failedShort}
                      onDownload={draft.download}
                      onResend={draft.resend}
                    />
                    <AttachmentRow
                      attachment={contract.file}
                      busy={contract.busy}
                      iconClass="text-primary"
                      failedLabel={CONTRACT_MESSAGES.failedShort}
                      onDownload={contract.download}
                      onResend={contract.resend}
                    />
                    <AttachmentRow
                      attachment={signed.file}
                      busy={signed.busy}
                      iconClass="text-green-600"
                      failedLabel={SIGNED_MESSAGES.failedShort}
                      onDownload={signed.download}
                      onResend={signed.resend}
                    />
                    <AttachmentRow
                      attachment={invoice.file}
                      busy={invoice.busy}
                      iconClass="text-amber-500"
                      failedLabel={INVOICE_MESSAGES.failedShort}
                      onDownload={invoice.download}
                      onResend={invoice.resend}
                    />
                  </div>

                  <input
                    ref={contract.inputRef}
                    type="file"
                    className="file-upload-input"
                    accept=".pdf,.doc,.docx"
                    onChange={contract.handleUpload}
                  />
                  <input
                    ref={invoice.inputRef}
                    type="file"
                    className="file-upload-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={invoice.handleUpload}
                  />
                  <input
                    ref={signed.inputRef}
                    type="file"
                    className="file-upload-input"
                    accept=".pdf"
                    onChange={signed.handleUpload}
                  />
                </div>

                {/* Los del equipo y los que trajo el influencer del formulario. */}
                <ArchivosColaboracion collab={collab} />

                <Button variant="destructive" size="sm" onClick={handleDelete} className="w-full">
                  <Trash2 className="h-3.5 w-3.5" />
                  Eliminar colaboración
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {pidiendoServicios && (
        <DialogoServicios
          inicial={collab.costeServicios}
          generando={generating}
          onCerrar={() => setPidiendoServicios(false)}
          onConfirmar={handleGenerateContract}
        />
      )}

      {showDetail && (
        <CollabDetailModal collab={collab} onClose={() => setShowDetail(false)} />
      )}
    </>
  );
}
